// --- Imports ---
import React, { useCallback, useEffect, useRef } from 'react';
import { useSmapFetch as useStrategyMapFetch } from '../../hooks/Smap/useSmap';

import { HiOutlineViewBoards } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import CustomButton from '../../components/common/CustomButton';
import RiskMitigationOnboarding from '../../pages/DashboardNew/components/RiskMitigationOnboarding';
import { useStrategyMapService } from '../../services/strategyMap';
import { errorHandler } from '../../utils/errorHandler';
import WorkUnitThumbnail from '../WorkUnits/components/WorkUnitsCard';
import { WorkUnitsCardSkeleton } from '../WorkUnits/components/WorkUnitsCardSkeleton';
import './StrategyMapList.css';

// --- Types & Interfaces ---
interface SmapListProps {
  onEdit: (data: any) => void;
  onDelete: (data: any) => void;
  onCreate: () => void;
  searchTerm?: string;
  searchType?: string;
  sortMap?: string | null;
  source?: string | null;
  refreshKey?: number;
}

/**
 * SmapList Component
 * Displays a list of strategic maps with infinite scroll support.
 */
const StrategyMapList: React.FC<SmapListProps> = ({
  onEdit,
  onDelete,
  onCreate,
  searchTerm = '',
  searchType = 'smap',
  sortMap = null,
  source = null,
  refreshKey = 0
}) => {
  // --- States & Data Fetching ---
  const {
    loading,
    loadingMore,
    data,
    total,
    page,
    totalPages,
    loadStrategyMaps,
    refreshStrategyMaps
  } = useStrategyMapFetch();


  // -- Services

  const { deleteStrategyMap } = useStrategyMapService();
  // --- Refs ---
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // --- Effects: Initial Load ---
  useEffect(() => {
    loadStrategyMaps(1, searchTerm, searchType, sortMap, source);
  }, [searchTerm, searchType, sortMap, source]);
  
  // --- Effects: Refresh Load ---
  useEffect(() => {
    if (refreshKey > 0) {
      refreshStrategyMaps(searchTerm, searchType, sortMap, source);
    }
  }, [refreshKey]);

  // --- Callbacks: Load More Logic ---
  const handleLoadMore = useCallback(() => {
    if (page < totalPages && !loading && !loadingMore) {
      loadStrategyMaps(page + 1, searchTerm, searchType, sortMap, source);
    }
  }, [page, totalPages, loading, loadingMore, searchTerm, searchType, sortMap, source]);

  // --- Effects: Infinite Scroll Observer ---
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && page < totalPages && !loading && !loadingMore) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [page, totalPages, loading, loadingMore]);


  // -- Handlers

  const handleDelete = async (strategymap_id: string) => {
    try {
   
     await deleteStrategyMap(strategymap_id)
   
      refreshStrategyMaps(searchTerm, searchType, sortMap, source);
    } catch (error) {
      errorHandler.showError(error);
    }
  };


  
  // --- Render Functions ---
  return (
    <div className="smap-list-container">
      <div ref={scrollContainerRef} className="smap-list-scroll-area">
        {/* Initial Loading State */}
        {loading ? (
          <div className="smap-list-skeleton-grid">
            {[...Array(8)].map((_, i) => (
              <WorkUnitsCardSkeleton key={i} />
            ))}
          </div>
        ) : data.length > 0 ? (
          /* Feature Content: Listing */
          <div>
            <div className="smap-list-grid">
              {data.map((item: any) => (
                <Link to={`/strategymaps/${item.id}`}>
                  <WorkUnitThumbnail
                    key={item.id}
                    data={item}
                    onEdit={onEdit}
                    onDelete={() => handleDelete(item.id)}
                  />
                </Link>
              ))}

              {/* Loading More Indicator */}
              {(loading || loadingMore) && (
                  [...Array(4)].map((_, i) => (
                    <WorkUnitsCardSkeleton key={`more-${i}`} />
                  ))
              )}
              
            </div>

            {/* Sentinel for Intersection Observer */}
            <div ref={loadMoreRef} className="smap-list-load-more-sentinel" />
          </div>
        ) : (
          /* Empty State */
          <div className="smap-list-empty-state">
            <HiOutlineViewBoards className="smap-list-empty-icon" />
            <h3 className="smap-list-empty-title">No strategy maps found</h3>
            <p className="smap-list-empty-description">
              Get started by creating your first strategy map to visualize
              roadmaps and planning.
            </p>
            <CustomButton onClick={onCreate} className="smap-list-create-btn">
              Create Strategy Map
            </CustomButton>
          </div>
        )}

        <RiskMitigationOnboarding />
      </div>
    </div>
  );
};

export default StrategyMapList;
